// Decides whether a page load on the admin host (admin.truwe.in) should be
// bounced back to /admin.
//
// The admin host only serves admin surfaces, EXCEPT for the editor previews
// (product page editor, CMS preview) which embed real storefront pages in an
// iframe on the same origin. Those iframes must render the storefront.
//
// Safety rules baked in here:
//  - Only a SAME-ORIGIN iframe can bypass the guard. A foreign site framing
//    admin.truwe.in gets the normal redirect, so the bypass can't be used to
//    render our admin host inside someone else's page.
//  - A top-level tab can never bypass, whatever the query string. `?editorPreview=1`
//    pasted into the address bar still lands on /admin.
//  - The decision is sticky per tab (sessionStorage) so client-side navigation
//    inside the preview iframe — which drops the query param — keeps rendering
//    the storefront instead of redirecting mid-session.

export const ADMIN_HOST_PREFIX = "admin.";
export const ADMIN_ALLOWED_PREFIXES = ["/admin", "/auth", "/api", "/_authenticated", "/account"];
export const PREVIEW_FLAG = "editorPreview";
export const PREVIEW_SESSION_KEY = "truwe:editor-preview-frame";

export function isAdminHost(host: string) {
  return host.startsWith(ADMIN_HOST_PREFIX);
}

export function isAdminAllowedPath(path: string) {
  return ADMIN_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}

export type PreviewFrameSignals = {
  /** Rendering inside any iframe. */
  framed: boolean;
  /** The embedding page is on the same origin as this document. */
  sameOriginAncestor: boolean;
  /** `?editorPreview=1` present on the current URL. */
  hasPreviewFlag: boolean;
  /** This tab was already recognised as a preview frame. */
  sticky: boolean;
};

/** A load counts as an editor preview only inside a same-origin iframe. */
export function isEditorPreviewFrame(s: PreviewFrameSignals) {
  if (!s.framed || !s.sameOriginAncestor) return false;
  return s.hasPreviewFlag || s.sticky;
}

export function shouldRedirectToAdmin(input: {
  host: string;
  path: string;
  preview: PreviewFrameSignals;
}) {
  if (!isAdminHost(input.host)) return false;
  if (isAdminAllowedPath(input.path)) return false;
  return !isEditorPreviewFrame(input.preview);
}

/** Reads the browser signals used by {@link shouldRedirectToAdmin}. */
export function readPreviewFrameSignals(): PreviewFrameSignals {
  if (typeof window === "undefined")
    return { framed: false, sameOriginAncestor: false, hasPreviewFlag: false, sticky: false };

  let framed = false;
  try {
    framed = window.self !== window.top;
  } catch {
    // Cross-origin parent throws on access — that is a foreign framer.
    return { framed: true, sameOriginAncestor: false, hasPreviewFlag: false, sticky: false };
  }

  const origin = window.location.origin;
  let sameOriginAncestor = false;
  if (framed) {
    const ancestors = (window.location as unknown as { ancestorOrigins?: DOMStringList })
      .ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      // Every ancestor must be same-origin — a same-origin parent nested in a
      // foreign page is still a foreign embed.
      sameOriginAncestor = Array.from({ length: ancestors.length }).every(
        (_, i) => ancestors.item(i) === origin,
      );
    } else if (document.referrer) {
      // Safari has no ancestorOrigins; fall back to the referrer origin.
      try {
        sameOriginAncestor = new URL(document.referrer).origin === origin;
      } catch {
        sameOriginAncestor = false;
      }
    }
  }

  const hasPreviewFlag = new URLSearchParams(window.location.search).has(PREVIEW_FLAG);

  let sticky = false;
  try {
    sticky = window.sessionStorage.getItem(PREVIEW_SESSION_KEY) === "1";
  } catch {
    sticky = false;
  }

  return { framed, sameOriginAncestor, hasPreviewFlag, sticky };
}

/** Remembers a confirmed preview frame for the rest of this tab's session. */
export function rememberPreviewFrame(signals: PreviewFrameSignals) {
  if (typeof window === "undefined") return;
  if (!isEditorPreviewFrame(signals) || signals.sticky) return;
  try {
    window.sessionStorage.setItem(PREVIEW_SESSION_KEY, "1");
  } catch {
    /* private mode — the query param still carries each load */
  }
}
